import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import InsightsIcon from "@mui/icons-material/Insights";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { API_URL } from "./config";
import { useNavigate, useParams } from "react-router-dom";

const AILessonTeacherPage = () => {
  const { lessonId } = useParams();
  //const navigate = useNavigate();
  const token = localStorage.getItem("auth") || localStorage.getItem("token");

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [gradingMap, setGradingMap] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [cloneForm, setCloneForm] = useState({
    TargetClassID: "",
    PublishMode: "DRAFT",
    AcademicYearLabel: "",
  });
  const [cloneBusy, setCloneBusy] = useState(false);

  const fetchTeacherLesson = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/api/ai-lesson-delivery/teacher/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLesson(res.data?.lesson || null);
      setAssignment(res.data?.assignment || null);
      setSubmissions(res.data?.submissions || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load lesson");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeacherLesson();
  }, [lessonId]);

  const handleOpenAttempt = async (attemptId) => {
    setError("");
    try {
      const res = await axios.get(`${API_URL}/api/ai-lesson-delivery/teacher/attempts/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSelectedAttempt(res.data?.attempt || null);
      setSelectedAnswers(res.data?.answers || []);

      const gm = {};
      (res.data?.answers || []).forEach((a) => {
        gm[a.AnswerId] = {
          TeacherScore: a.TeacherScore ?? a.FinalScore ?? 0,
          FeedbackText: a.FeedbackText || "",
        };
      });
      setGradingMap(gm);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to open attempt");
    }
  };

  const handleGradeChange = (answerId, field, value) => {
    setGradingMap((prev) => ({
      ...prev,
      [answerId]: {
        ...(prev[answerId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveGrade = async (answerId) => {
    const row = gradingMap[answerId] || {};
    try {
      await axios.post(
        `${API_URL}/api/ai-lesson-delivery/teacher/answers/${answerId}/grade`,
        {
          TeacherScore: row.TeacherScore || 0,
          FeedbackText: row.FeedbackText || "",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Grade saved");
      if (selectedAttempt?.AttemptId) {
        await handleOpenAttempt(selectedAttempt.AttemptId);
        await fetchTeacherLesson();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save grade");
    }
  };

  const handleFinalizeAttempt = async () => {
    if (!selectedAttempt?.AttemptId) return;

    try {
      await axios.post(
        `${API_URL}/api/ai-lesson-delivery/teacher/attempts/${selectedAttempt.AttemptId}/finalize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Attempt finalized");
      await handleOpenAttempt(selectedAttempt.AttemptId);
      await fetchTeacherLesson();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to finalize grading");
    }
  };

  const summary = useMemo(() => {
    const total = submissions.length;
    const submitted = submissions.filter((x) => x.Status === "SUBMITTED").length;
    const graded = submissions.filter((x) => x.Status === "GRADED").length;
    const inProgress = submissions.filter((x) => x.Status === "IN_PROGRESS").length;

    const avgCompletion =
      total > 0
        ? (
            submissions.reduce((sum, x) => sum + Number(x.CompletionPercent || 0), 0) / total
          ).toFixed(1)
        : "0.0";

    const avgScore =
      total > 0
        ? (
            submissions.reduce((sum, x) => sum + Number(x.TotalScore || 0), 0) / total
          ).toFixed(2)
        : "0.00";

    return { total, submitted, graded, inProgress, avgCompletion, avgScore };
  }, [submissions]);

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
              Teacher Lesson Review & Grading
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Review lesson, grade submissions, and post the lesson to another batch/class
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              startIcon={<InsightsIcon />}
              endIcon={<OpenInNewIcon />}
              onClick={() =>
                window.open(
                  `/AILessonAnalyticsPage/${lessonId}`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              Analytics
            </Button>

            {selectedAttempt?.AttemptId ? (
              <Button
                variant="contained"
                sx={primaryBtnSx}
                startIcon={<DoneAllIcon />}
                onClick={handleFinalizeAttempt}
              >
                Finalize Attempt
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          {loading ? (
            <Paper elevation={0} sx={{ ...surfaceSx, p: 3 }}>
              <Typography>Loading lesson...</Typography>
            </Paper>
          ) : !lesson ? (
            <Paper elevation={0} sx={{ ...surfaceSx, p: 3 }}>
              <Typography>No lesson found.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1.15rem", mb: 1 }}>
                    {lesson.LessonTitle}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={`Subject: ${lesson.SubjectCode || "-"}`} />
                    <Chip size="small" label={`Chapter: ${lesson.ChapterTitle || "-"}`} />
                    <Chip size="small" label={`ClassID: ${lesson.ClassID || "-"}`} />
                    <Chip size="small" label={`Branch: ${lesson.BranchCode || "-"}`} />
                    <Chip size="small" label={`Semester: ${lesson.Semester || "-"}`} />
                    <Chip size="small" label={`Section: ${lesson.Section || "-"}`} />
                    <Chip size="small" label={`Published: ${lesson.ReviewStatus || "-"}`} />
                    <Chip size="small" label={`Submissions: ${summary.total}`} />
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                    Learning Objectives
                  </Typography>
                  {(lesson?.LessonJson?.learningObjectives || []).map((obj, idx) => (
                    <Typography key={idx}>• {typeof obj === "string" ? obj : obj?.text || ""}</Typography>
                  ))}
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                    Quick Snapshot
                  </Typography>

                  <Stack spacing={1}>
                    <Typography><b>Total Attempts:</b> {summary.total}</Typography>
                    <Typography><b>In Progress:</b> {summary.inProgress}</Typography>
                    <Typography><b>Submitted:</b> {summary.submitted}</Typography>
                    <Typography><b>Graded:</b> {summary.graded}</Typography>
                    <Typography><b>Avg Completion:</b> {summary.avgCompletion}%</Typography>
                    <Typography><b>Avg Total Score:</b> {summary.avgScore}</Typography>
                  </Stack>
                </Paper>

              </Grid>

              <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Typography sx={{ fontWeight: 900, color: BLUE, mb: 2 }}>
                    Student Submissions
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>StudentID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Completion %</TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell>Max</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {submissions.map((row, idx) => (
                          <TableRow key={row.AttemptId} sx={zebraRowSx(idx)}>
                            <TableCell>{row.StudentID}</TableCell>
                            <TableCell>{row.Status}</TableCell>
                            <TableCell>{row.CompletionPercent}</TableCell>
                            <TableCell>{row.TotalScore}</TableCell>
                            <TableCell>{row.MaxScore}</TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={outlinedBtnSx}
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleOpenAttempt(row.AttemptId)}
                              >
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!submissions.length && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography color="text.secondary">
                                No submissions yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {selectedAttempt?.AttemptId ? (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ mb: 2 }}
                    >
                      <Typography sx={{ fontWeight: 900, color: BLUE }}>
                        Grading Attempt: {selectedAttempt.StudentID}
                      </Typography>
                      <Button
                        variant="contained"
                        sx={primaryBtnSx}
                        startIcon={<DoneAllIcon />}
                        onClick={handleFinalizeAttempt}
                      >
                        Finalize Attempt
                      </Button>
                    </Stack>

                    <Stack spacing={2}>
                      {selectedAnswers.map((ans, idx) => (
                        <Paper key={ans.AnswerId} elevation={0} sx={{ p: 2, border: `1px solid ${BORDER}` }}>
                          <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                            Q{idx + 1}. {ans.QuestionText}
                          </Typography>

                          <Typography sx={{ color: "text.secondary", mb: 1 }}>
                            Type: {ans.QuestionType} | Max: {ans.MaxScore} | Auto: {ans.AutoScore} | Final: {ans.FinalScore}
                          </Typography>

                          <Typography sx={{ mb: 1 }}>
                            <b>Student Answer:</b> {ans.AnswerText || "-"}
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <TextField
                                label="Teacher Score"
                                type="number"
                                size="small"
                                fullWidth
                                value={gradingMap[ans.AnswerId]?.TeacherScore ?? 0}
                                onChange={(e) => handleGradeChange(ans.AnswerId, "TeacherScore", e.target.value)}
                                sx={inputSx}
                              />
                            </Grid>
                            <Grid item xs={12} md={7}>
                              <TextField
                                label="Feedback"
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                value={gradingMap[ans.AnswerId]?.FeedbackText ?? ""}
                                onChange={(e) => handleGradeChange(ans.AnswerId, "FeedbackText", e.target.value)}
                                sx={inputSx}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Button
                                fullWidth
                                variant="contained"
                                sx={primaryBtnSx}
                                startIcon={<SaveIcon />}
                                onClick={() => handleSaveGrade(ans.AnswerId)}
                              >
                                Save
                              </Button>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ) : null}
            </Grid>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AILessonTeacherPage;